import { CopyToClipboardColumn } from '@atoms/CopyToClipboardColumn';
import { Column, EditComponentProps } from '@material-table/core';
import { DbAthlete, STATUS, TeamAthlete, User } from '@type/db-types';
import { calculateAge, calculateYearsExperience, readableDateFromDate } from './date-utils';
import { STATUS_LOOKUP } from './team-table-utils';
import RenderStatusTag from '@atoms/RenderStatusTag';
import TeamStatusSelector from '@atoms/TeamStatusSelector';
import useTeams from '@hooks/useTeams';
import TeamRoleSelector from '@atoms/TeamRoleSelector';

interface ExtendedUser extends User {
    userId?: string; // view key
    athleteId?: string; // view key
    userKey: UserIdKey;
}

type UserIdKey = 'id' | 'userId' | 'athleteId';

export const userColumns: (
    userKey: UserIdKey,
    showContact: boolean,
    viewContact: (userId: string) => boolean,
) => Column<ExtendedUser>[] = (userKey, showContact, viewContact) => [
    { title: 'First Name', field: 'firstName' },
    { title: 'Last Name', field: 'lastName' },
    ...(showContact
        ? [
              {
                  title: 'Email',
                  field: 'email',
                  render: (data: ExtendedUser) =>
                      viewContact(data[userKey] || '') && (
                          <CopyToClipboardColumn text={data.email || ''} />
                      ),
              },
              {
                  title: 'Phone',
                  field: 'phone',
                  hidden: true,
                  render: (data: ExtendedUser) =>
                      data.phone && viewContact(data[userKey] || '') ? (
                          <CopyToClipboardColumn text={data.phone} />
                      ) : (
                          '...'
                      ),
              },
              {
                  field: 'userId',
                  filtering: false,
                  hidden: true,
                  title: 'ID',
                  render: (data: ExtendedUser) => (
                      <CopyToClipboardColumn text={data[userKey] || data.userId || data.id || ''} />
                  ),
              },
              {
                  title: 'Created At',
                  hidden: true,
                  field: 'createdAt',
                  render: ({ createdAt }: ExtendedUser) =>
                      createdAt ? readableDateFromDate(createdAt) : createdAt,
                  type: 'datetime' as const,
              },
              {
                  title: 'Updated At',
                  hidden: true,
                  field: 'updatedAt',
                  render: ({ updatedAt }: ExtendedUser) =>
                      updatedAt ? readableDateFromDate(updatedAt) : updatedAt,
                  type: 'datetime' as const,
              },
          ]
        : []),
];

export function editableUserColumns<T extends ExtendedUser>({
    userKey = 'id',
    hideEmail = false,
    hideRole = false,
    hideTeamStatus = false,
}: {
    userKey?: UserIdKey;
    hideEmail?: boolean;
    hideRole?: boolean;
    hideTeamStatus?: boolean;
}): Column<T>[] {
    return [
        ...userColumns(userKey, false, () => false),
        EDIT_EMAIL(hideEmail),
        EDIT_TEAM_STATUS(hideTeamStatus),
        EDIT_TEAM_ROLE(hideRole),
        EDIT_PHONE(),
    ] as unknown as Column<T>[];
}

export function EDIT_EMAIL<T extends { email: string | null; userStatus: STATUS }>(
    hidden = false,
): Column<T> {
    // allow editing of email only if the user is not active
    return {
        editable: (_columnDe, rowData) => rowData.userStatus !== STATUS.ACTIVE,
        field: 'email',
        hidden,
        render: (data) => <CopyToClipboardColumn text={data.email || ''} />,
        title: 'Email',
    };
}
export function EDIT_STATUS<T extends object>(
    hidden = false,
    field: keyof T,
    editComponent: (props: EditComponentProps<T>) => JSX.Element,
): Column<T> {
    return {
        editable: (_columnDe: unknown, rowData: T) => rowData?.[field] !== STATUS.ACTIVE,
        // editable: (_columnDe: T, rowData: T) => rowData?.[field] !== STATUS.ACTIVE,
        editComponent,
        hidden,
        lookup: STATUS_LOOKUP,
        field: field,
        render: (rowData) => <RenderStatusTag teamStatus={rowData?.[field] as STATUS} />,
        title: 'Status',
    };
}

export function EDIT_TEAM_STATUS<T extends object>(
    hidden = false,
    field: keyof T = 'teamStatus' as keyof T,
): Column<T> {
    const { refreshMembers } = useTeams();

    return EDIT_STATUS<T>(hidden, field, (props: EditComponentProps<T>) => (
        <TeamStatusSelector
            user={props.rowData as Pick<TeamAthlete, 'id' | 'membershipId' | 'teamStatus'>}
            refresh={refreshMembers}
        />
    ));
}

export function EDIT_ROLE<T extends object>(
    field: keyof T,
    editComponent: (props: EditComponentProps<T>) => JSX.Element,
): Column<T> {
    return {
        editable: (_columnDe: unknown, rowData: T) => rowData?.[field] !== STATUS.ACTIVE,
        // editable: (_columnDe: T, rowData: T) => rowData?.[field] !== STATUS.ACTIVE,
        editComponent,
        lookup: STATUS_LOOKUP,
        field: field,
        render: (rowData) => <RenderStatusTag teamStatus={rowData?.[field] as STATUS} />,
        title: 'Status',
    };
}

export function EDIT_TEAM_ROLE<T extends TeamAthlete>(
    hidden = true,
    field: keyof T = 'role' as keyof T,
): Column<T> {
    const { refreshMembers } = useTeams();

    return {
        editable: (_columnDe: unknown, rowData: T) => rowData?.[field] !== STATUS.ACTIVE,
        // editable: (_columnDe: T, rowData: T) => rowData?.[field] !== STATUS.ACTIVE,
        editComponent: (props) => (
            <TeamRoleSelector user={props.rowData} refresh={refreshMembers} />
        ),
        lookup: STATUS_LOOKUP,
        hidden,
        field: field,
        render: (user) => <TeamRoleSelector user={user} refresh={refreshMembers} />,
        title: 'Role',
    };
}

export function EDIT_PHONE<T extends { phone: string }>(): Column<T> {
    return {
        title: 'Phone',
        field: 'phone',
        hidden: true,
        render: (data) => (data.phone ? <CopyToClipboardColumn text={data.phone} /> : '...'),
    };
}

export function AGE_COLUMN<T extends DbAthlete>(hidden = false): Column<T> {
    return {
        title: 'Age',
        field: 'birthday',
        filtering: false,
        hidden,
        render: (data) => (data.birthday ? calculateAge(data.birthday) : ''),
    };
}

export function EXPERIENCE_COLUMN<T extends DbAthlete>(hidden = false): Column<T> {
    return {
        title: 'Years Experience',
        field: 'activeSince',
        filtering: false,
        hidden,
        render: (data) => calculateYearsExperience(data.activeSince),
    };
}
